import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/Layout';
import api from '../lib/api';
import { Headphones, BookOpen, PenTool, Mic, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, BarChart, Bar } from 'recharts';

export default function Performance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/performance').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardLayout title="Performance"><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div></DashboardLayout>;

  const scores = data?.latest_scores || { listening: 0, reading: 0, writing: 0, speaking: 0 };
  const history = (data?.history || []).slice().reverse();

  const radarData = [
    { skill: 'Listening', score: scores.listening, fullMark: 12 },
    { skill: 'Reading', score: scores.reading, fullMark: 12 },
    { skill: 'Writing', score: scores.writing, fullMark: 12 },
    { skill: 'Speaking', score: scores.speaking, fullMark: 12 },
  ];

  const lineData = history.map((h, i) => ({
    name: `Exam ${i + 1}`,
    Listening: h.listening,
    Reading: h.reading,
    Writing: h.writing,
    Speaking: h.speaking,
    Overall: h.overall,
  }));

  const trend = (skill) => {
    if (history.length < 2) return 'neutral';
    const last = history[history.length - 1]?.[skill] || 0;
    const prev = history[history.length - 2]?.[skill] || 0;
    if (last > prev) return 'up';
    if (last < prev) return 'down';
    return 'neutral';
  };

  const TrendIcon = ({ dir }) => {
    if (dir === 'up') return <TrendingUp size={14} className="text-green-500" />;
    if (dir === 'down') return <TrendingDown size={14} className="text-red-500" />;
    return <Minus size={14} className="text-neutral-400" />;
  };

  return (
    <DashboardLayout title="Performance Analytics">
      <div className="space-y-6" data-testid="performance-page">
        {/* Score Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'listening', label: 'Listening', icon: <Headphones size={16} />, color: 'text-blue-600 bg-blue-50 border-blue-200' },
            { key: 'reading', label: 'Reading', icon: <BookOpen size={16} />, color: 'text-amber-600 bg-amber-50 border-amber-200' },
            { key: 'writing', label: 'Writing', icon: <PenTool size={16} />, color: 'text-rose-600 bg-rose-50 border-rose-200' },
            { key: 'speaking', label: 'Speaking', icon: <Mic size={16} />, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
          ].map(s => (
            <div key={s.key} className={`p-4 rounded-xl border ${s.color}`} data-testid={`perf-score-${s.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-sm opacity-80">{s.icon}{s.label}</div>
                <TrendIcon dir={trend(s.key)} />
              </div>
              <div className="text-3xl font-bold">CLB {scores[s.key] || '—'}</div>
            </div>
          ))}
        </div>

        {/* Charts */}
        {data?.total_exams > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="perf-radar">
              <h3 className="font-semibold text-neutral-900 mb-4">Skills Radar</h3>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="skill" tick={{ fill: '#64748B', fontSize: 12 }} />
                  <Radar dataKey="score" fill="#2563EB" fillOpacity={0.2} stroke="#2563EB" strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-neutral-200 p-6" data-testid="perf-trend">
              <h3 className="font-semibold text-neutral-900 mb-4">Score Trends</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={lineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                  <YAxis domain={[0, 12]} tick={{ fontSize: 11, fill: '#94A3B8' }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Listening" stroke="#3B82F6" strokeWidth={2} />
                  <Line type="monotone" dataKey="Reading" stroke="#F59E0B" strokeWidth={2} />
                  <Line type="monotone" dataKey="Writing" stroke="#EF4444" strokeWidth={2} />
                  <Line type="monotone" dataKey="Speaking" stroke="#10B981" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center">
            <p className="text-neutral-500 text-lg mb-2">No exam data yet</p>
            <p className="text-neutral-400 text-sm">Complete a mock exam to see your performance analytics here.</p>
          </div>
        )}

        {/* Exam History */}
        {history.length > 0 && (
          <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden" data-testid="exam-history-table">
            <div className="p-4 border-b border-neutral-200">
              <h3 className="font-semibold text-neutral-900">Exam History</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                    <th className="text-center px-4 py-3 font-medium">Listening</th>
                    <th className="text-center px-4 py-3 font-medium">Reading</th>
                    <th className="text-center px-4 py-3 font-medium">Writing</th>
                    <th className="text-center px-4 py-3 font-medium">Speaking</th>
                    <th className="text-center px-4 py-3 font-medium">Overall</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {history.map((h, i) => (
                    <tr key={i} className="hover:bg-neutral-50">
                      <td className="px-4 py-3">{h.date ? new Date(h.date).toLocaleDateString() : 'N/A'}</td>
                      <td className="text-center px-4 py-3 font-medium">{h.listening || '—'}</td>
                      <td className="text-center px-4 py-3 font-medium">{h.reading || '—'}</td>
                      <td className="text-center px-4 py-3 font-medium">{h.writing || '—'}</td>
                      <td className="text-center px-4 py-3 font-medium">{h.speaking || '—'}</td>
                      <td className="text-center px-4 py-3 font-bold text-primary">{h.overall || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <p className="text-sm text-neutral-500 mb-1">Total Exams Completed</p>
            <p className="text-3xl font-bold text-neutral-900">{data?.total_exams || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <p className="text-sm text-neutral-500 mb-1">Total Questions Answered</p>
            <p className="text-3xl font-bold text-neutral-900">{data?.total_questions_answered || 0}</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
